import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaUsers,
  FaClipboardCheck,
  FaComments,
  FaFileExport,
  FaRocket,
  FaClock,
  FaFileAlt,
  FaChartLine,
  FaArrowRight,
  FaQuoteLeft
} from "react-icons/fa";
import logo from "../assets/ezOCM_Logo.png";
import RegistrationModal from "../components/RegistrationModal";

export default function FreeLandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => setIsModalOpen(false);

  // Fire page view conversion when landing page loads
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17675232341/5yXPCPeqybIbENXImuxB',
        'value': 1.0,
        'currency': 'CAD'
      });
    }
  }, []);

  // Track registration button clicks and open modal
  const openModal = () => {
    // Fire conversion for registration button click
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17675232341/X1LJCO3LsrQbENXImuxB'
      });
    }

    // Open the registration modal
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-white m-0 p-0">
      <style jsx global>{`
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          scroll-behavior: smooth;
        }

        .feature-card {
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px -6px rgba(59, 130, 246, 0.15);
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>

      {/* Registration Modal */}
      <RegistrationModal isOpen={isModalOpen} onClose={closeModal} />

      {/* Header - Minimal */}
      <header className="fixed top-0 left-0 right-0 z-40 w-full bg-white/98 backdrop-blur-md shadow-sm" style={{margin: '0', padding: '0', position: 'fixed', top: '0'}}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center">
            <img src={logo} alt="ezOCM" className="h-16 w-auto" />
          </div>

          <div className="flex items-center gap-3">
            <a href="/signin" className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200">
              Sign In
            </a>
            <button
              onClick={openModal}
              className="px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Start Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden" style={{marginTop: '76px', paddingTop: '80px', paddingBottom: '80px'}}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8 animate-fadeIn">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                From Blank Page to
                <span className="text-blue-600"> Client-Ready Change Plan</span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed">
                Manage stakeholders, run assessments, generate polished comms—all from one platform. <strong>Free to start.</strong>
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openModal}
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-blue-600 text-white font-semibold text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Start Free Account
                  <FaArrowRight className="ml-2" />
                </button>
                <a href="#how-it-works" className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-gray-300 text-gray-700 font-semibold text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200">
                  See How It Works
                </a>
              </div>

              <p className="text-sm text-gray-500">
                ✓ Start one project free • ✓ No credit card required • ✓ Upgrade only when you need more
              </p>
            </div>

            {/* Right Column - Dashboard Screenshot */}
            <div className="relative animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                  </div>
                  <div className="flex-1 text-center text-white text-sm font-medium">ezOCM Platform</div>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
                  alt="ezOCM Dashboard - Stakeholder Mapping & Assessments"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-lg text-gray-600 mb-6">
              You're an independent change consultant. You juggle tools, templates, emails—and <strong>it's slowing you down.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Pain Point 1 */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-red-200 hover:shadow-lg transition-all">
              <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                <FaClock className="text-red-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Time Lost Stitching Tools</h3>
              <p className="text-gray-600 leading-relaxed">
                Spreadsheets, slides, survey tools, email—you waste hours moving data between disconnected systems.
              </p>
            </div>

            {/* Pain Point 2 */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-orange-200 hover:shadow-lg transition-all">
              <div className="w-16 h-16 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                <FaFileAlt className="text-orange-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Blank-Page Fatigue</h3>
              <p className="text-gray-600 leading-relaxed">
                Every new client means starting from scratch on comms, assessments, and stakeholder plans.
              </p>
            </div>

            {/* Pain Point 3 */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 hover:border-yellow-200 hover:shadow-lg transition-all">
              <div className="w-16 h-16 rounded-xl bg-yellow-100 flex items-center justify-center mb-4">
                <FaChartLine className="text-yellow-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Hard to Show Impact</h3>
              <p className="text-gray-600 leading-relaxed">
                Demonstrating people-side progress with professional deliverables requires bulky, expensive software.
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 mb-6">
              You deserve a better way.
            </p>
            <button
              onClick={openModal}
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-blue-600 text-white font-semibold text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg"
            >
              Try ezOCM Free
            </button>
          </div>
        </div>
      </section>

      {/* Features / Solution Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What ezOCM Delivers for You
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything an independent consultant needs to deliver client-ready outcomes—without the enterprise bloat
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Feature 1 */}
            <div className="feature-card p-10 rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6 shadow-lg">
                <FaUsers className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Stakeholder Mapping & Strategy</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Import, tag by influence/interest/attitude, and get AI-suggested engagement approaches. No more spreadsheet chaos.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Visual stakeholder maps</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Auto-suggest engagement tactics</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Track attitudes over time</span>
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className="feature-card p-10 rounded-3xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6 shadow-lg">
                <FaClipboardCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI-Powered Assessments</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Build readiness or impact assessments unique to your client. Send guest links, track responses in real-time.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>AI-generated survey questions</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Guest access (no account needed)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Real-time response tracking</span>
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="feature-card p-10 rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg">
                <FaComments className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comms & Content Generation</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Message architecture, comms calendar, AI-drafted emails, talking points, FAQs—publish in minutes, not days.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>AI-generated messages & FAQs</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Comms calendar with themes</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Brand-ready talking points</span>
                </li>
              </ul>
            </div>

            {/* Feature 4 */}
            <div className="feature-card p-10 rounded-3xl bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mb-6 shadow-lg">
                <FaFileExport className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Polished Exportable Plans</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Generate brandable DOCX change & comms plans you hand to sponsors. Look like an enterprise consultancy.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Export to Word format</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Customizable templates</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                  <span>Professional branding</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Project-Scale Fit */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-12 text-center text-white shadow-2xl">
            <FaRocket className="text-5xl mx-auto mb-6 opacity-90" />
            <h3 className="text-3xl font-bold mb-4">
              Project-Scale Fit for a Boutique
            </h3>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
              Run multiple small engagements with ease. No heavy software. No enterprise licensing. Upgrade when you scale.
            </p>
            <button
              onClick={openModal}
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-blue-600 font-semibold text-lg hover:bg-gray-100 transition-all duration-200 shadow-lg"
            >
              Start Your First Project Free
            </button>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Here's What Change Leaders Are Saying
            </h2>
          </div>

          <div className="grid md:grid-cols-1 gap-8 max-w-4xl mx-auto">
            {/* Testimonial */}
            <div className="bg-white p-10 rounded-3xl shadow-xl border-2 border-blue-100">
              <FaQuoteLeft className="text-blue-600 text-4xl mb-6 opacity-50" />
              <p className="text-xl text-gray-700 leading-relaxed mb-6 italic">
                "The Change Management Platform has been a true game changer for our organization in how we understand and manage the impact of change on our people."
              </p>
              <div className="border-t pt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl">
                  E
                </div>
                <div>
                  <p className="font-bold text-gray-900">ezOCM Customer</p>
                  <p className="text-sm text-gray-600">Change Management Leader</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              <strong>Repeatable assets in &lt; 1 day</strong> • Trusted by independent consultants worldwide
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Your Workflow in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From sign-up to client-ready deliverables—ezOCM streamlines your entire change management process
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-blue-600 text-white flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Create Free Account</h3>
              <p className="text-gray-600">
                Sign up in 60 seconds. No credit card. No setup fees. Start immediately.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-600 text-white flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Launch Your Project</h3>
              <p className="text-gray-600">
                Import stakeholders, tag them, and build your first assessment.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-purple-600 text-white flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Generate & Track</h3>
              <p className="text-gray-600">
                Send assessment links, track responses, generate comms, export plans.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-orange-600 text-white flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Upgrade When Ready</h3>
              <p className="text-gray-600">
                Scale to unlimited projects when your consultancy grows.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={openModal}
              className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition-all duration-200 shadow-2xl hover:shadow-3xl"
            >
              Start Free Account Now
              <FaArrowRight className="ml-3" />
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Single-project free • No setup • No heavy software • Just results
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Leave Blank-Page Fatigue Behind?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join independent consultants using ezOCM to deliver premium change management outcomes—faster and more professionally than ever.
          </p>
          <button
            onClick={openModal}
            className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-white text-blue-600 font-bold text-xl hover:bg-gray-100 transition-all duration-200 shadow-2xl"
          >
            Start Your Free Account
            <FaArrowRight className="ml-3" />
          </button>
          <p className="text-blue-200 text-sm mt-6">
            No credit card required • Start managing your first project free • Upgrade when you scale
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img src={logo} alt="ezOCM" className="h-8 w-auto brightness-0 invert" />
              <p className="text-sm text-gray-400">
                ezOCM – Everything you need for the people side of change—without the enterprise bloat.
              </p>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <a href="/contact" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm text-gray-500">
            © 2025 ezOCM. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
